import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Notification, VideoSummary } from '@streamr/shared';
import { api } from '../client';
import { routes } from '../routes';
import { usePagedQuery } from './useDiscovery';

/**
 * A notification as the bell renders it. The shared DTO carries ids only
 * (`videoId`, `channelId`); the list route hydrates them, because a bell that
 * says "new video" with no title and no thumbnail is not worth opening.
 */
export type NotificationItem = Notification & {
  video?: Pick<VideoSummary, 'id' | 'title' | 'posterUrl'> | null;
  channel?: { id: string; handle: string; displayName: string; avatarUrl: string | null } | null;
};

export const useNotifications = (enabled: boolean) =>
  usePagedQuery<NotificationItem>(['notifications'], routes.notifications.list(), {}, { enabled, limit: 20 });

/**
 * Just the badge number. Polled on a slow interval rather than pushed: v1 has
 * no websocket, and a minute of staleness on a badge is not worth one.
 */
export const useUnreadCount = (enabled: boolean) =>
  useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: ({ signal }) => api.get<{ count: number }>(routes.notifications.unreadCount(), { signal }),
    enabled,
    refetchInterval: 60_000,
    // A bell that fails is not worth an error state; it just shows nothing.
    retry: false,
  });

export const useMarkNotificationsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => api.post<void>(routes.notifications.read(), { ids }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['notifications'] });
    },
  });
};
